import React, { useMemo, useState } from 'react';
import { FileTextIcon, PlusIcon } from 'lucide-react';
import { Quote, QuoteStatus } from '../../types/pro';
import { QUOTE_STATUS, formatDate, kr, quoteTotal } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProQuotesProps {
  quotes: Quote[];
  vatRate: number;
  onOpen: (quoteId: string) => void;
  onNew: () => void;
}
export function ProQuotes({
  quotes,
  vatRate,
  onOpen,
  onNew
}: ProQuotesProps) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<QuoteStatus | 'alle'>('alle');
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return quotes.filter(quote => status === 'alle' || quote.status === status).filter(quote => term ? [quote.quoteNo, quote.customerName, quote.title].join(' ').toLowerCase().includes(term) : true).sort((a, b) => b.createdDate.localeCompare(a.createdDate));
  }, [quotes, query, status]);
  const open = quotes.filter(quote => ['sendt', 'set'].includes(quote.status));
  const openValue = open.reduce((sum, quote) => sum + quoteTotal(quote, vatRate), 0);
  return <div data-id="element-2037" data-mp="me05v6c1mbaw">
      <PageHeader title="Tilbud" subtitle={`${open.length} ude hos kunden · ${kr(openValue)}`} actions={<button onClick={onNew} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-2039" data-mp="cnouiyq14ztz">
            <PlusIcon className="h-4 w-4" data-id="element-2040" data-mp="15bt1roo91kys" />
            Nyt tilbud
          </button>} data-id="element-2038" data-mp="me9wmw4wwmhn" />

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-2041" data-mp="1a3b004yw17g2">
        <SearchField value={query} onChange={setQuery} placeholder="Søg tilbudsnummer eller kunde…" className="min-w-[220px] flex-1" data-id="element-2042" data-mp="1a4vww18od2h3" />
        <label data-id="element-2043" data-mp="1f0q86t64e5ze">
          <span className="sr-only" data-id="element-2044" data-mp="1ey40mtj8bqh6">Filtrer status</span>
          <select value={status} onChange={e => setStatus(e.target.value as QuoteStatus | 'alle')} className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500" data-id="element-2045" data-mp="19zka0ftoxqih">
            <option value="alle" data-id="element-2046" data-mp="w8xboorue2mx">Alle statusser</option>
            {(Object.keys(QUOTE_STATUS) as QuoteStatus[]).map(key => <option key={key} value={key} data-id="element-2047" data-mp="rb5ilrfgoah5">
                {QUOTE_STATUS[key].label}
              </option>)}
          </select>
        </label>
      </div>

      {rows.length === 0 ? <EmptyState icon={FileTextIcon} title="Ingen tilbud matcher" description="Lav et tilbud fra et lead eller direkte herfra." data-id="element-2048" data-mp="hboh7m78siqz" /> : <div className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-2049" data-mp="2owwp72vvvnsc">
          <table className="w-full text-sm" data-id="element-2050" data-mp="2sys0n07j3hju">
            <thead className="hidden sm:table-header-group" data-id="element-2051" data-mp="2sxqf21svzcnv">
              <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-2052" data-mp="2xzo1t60hwplk">
                {['Nummer', 'Kunde', 'Beskrivelse', 'Beløb', 'Oprettet', 'Status'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-2053" data-mp="25b0ez603ls5w">
                      {heading}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-2054" data-mp="1mjhasvuc882a">
              {rows.map(quote => <tr key={quote.id} onClick={() => quote.id && onOpen(quote.id)} className="cursor-pointer transition-colors hover:bg-stone-50" data-id="element-2055" data-mp="1rikg8he8cgas">
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-stone-500" data-id="element-2056" data-mp="3p6efylr3q2ux">
                    {quote.quoteNo}
                  </td>
                  <td className="px-4 py-3 font-medium text-stone-900" data-id="element-2057" data-mp="6tgeubikp3z6">
                    {quote.customerName}
                  </td>
                  <td className="hidden max-w-[260px] truncate px-4 py-3 text-stone-600 sm:table-cell" data-id="element-2058" data-mp="gq2xctnffiy1">
                    {quote.title}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums text-stone-900" data-id="element-2059" data-mp="bqypc6c8nr9a">
                    {kr(quoteTotal(quote, vatRate))}
                  </td>
                  <td className="hidden whitespace-nowrap px-4 py-3 text-stone-500 sm:table-cell" data-id="element-2060" data-mp="kt7wqnc7n4ig">
                    {formatDate(quote.createdDate)}
                  </td>
                  <td className="px-4 py-3" data-id="element-2061" data-mp="2mk2wtrdg9puz">
                    <StatusChip meta={QUOTE_STATUS[quote.status]} data-id="element-2062" data-mp="2ml47huqk0ac6" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </div>;
}